import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';
import { CreateChancel, DateCalendarForm, ErrMessage, Icd10CodeSelect, Input, SwitchCustomized } from 'components';
import { clientDiagnosisActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import {
    FindErrorItem,
    FindLoad,
    FindSuccessItem,
    formatDiagnosisCode,
    ICD10_CM_CODE_PATTERN,
    Images,
    MAX_DIAGNOSIS_CODE_LENGTH,
    MAX_DIAGNOSIS_DESCRIPTION_LENGTH,
    useModal,
    useSecondaryModal,
} from 'utils';
import { CREATE_DIAGNOSIS_ACTION, EDIT_DIAGNOSIS_ACTION } from './constants';

/**
 * Add / edit one client diagnosis. The code is picked from the official ICD-10-CM list (official
 * description from the api), or — with the picker's "Add Diagnosis Code" — typed in with its own
 * description when it is not in that list. The code is fixed once saved: a different code is a
 * different diagnosis.
 * End date is optional (most diagnoses never end); status follows the dates.
 *
 * Opened from the Diagnoses tab, and — with `secondary` — on top of the appointment modal or the
 * billing list, where `clientId` / `acceptedCodes` come from the appointment being fixed.
 */
export const AddDiagnosisModal = ({ info, clientId: clientIdProp, acceptedCodes, defaultStartDate, secondary, onSaved }) => {
    const ACTION_TYPE = info ? EDIT_DIAGNOSIS_ACTION : CREATE_DIAGNOSIS_ACTION;
    const dispatch = useDispatch();
    const client = useSelector((state) => state.client.clientItemInfo);
    const diagnoses = useSelector((state) => state.clientDiagnosis.clientDiagnoses);
    const clientId = clientIdProp || info?.client || client?.id;
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const { close } = useModal();
    const { closeSecondary } = useSecondaryModal();
    const closeModal = secondary ? closeSecondary : close;
    // Typed-in code (not in ICD-10-CM). `shouldUnregister` drops the hidden field's value and rules when
    // switching between the picker and the typed code.
    const [customCode, setCustomCode] = useState(false);
    const { handleSubmit, control, reset, watch, setValue } = useForm({
        shouldUnregister: true,
        defaultValues: { code: '', startDate: defaultStartDate || null, endDate: null, isPrimary: false },
    });

    const startCustomCode = (typed) => {
        const looksLikeCode = ICD10_CM_CODE_PATTERN.test((typed || '').toUpperCase());
        // Set before the typed fields mount: they pick the values up when they register
        setValue('customCode', looksLikeCode ? typed.toUpperCase() : '');
        setValue('customDescription', looksLikeCode ? '' : typed || '');
        setCustomCode(true);
    };

    // The Diagnoses tab has the list loaded; on top of an appointment it may belong to another client.
    const otherPrimary = clientIdProp ? null : diagnoses?.find((diagnosis) => diagnosis?.isPrimary && diagnosis?.id !== info?.id);

    useEffect(() => {
        dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        if (info) {
            reset({
                code: info.code,
                startDate: info.startDate,
                endDate: info.endDate || null,
                isPrimary: !!info.isPrimary,
            });
        }
    }, [info]);

    useEffect(() => {
        if (!success) return;
        dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        if (onSaved) onSaved(success?.data);
        closeModal();
    }, [success]);

    const handleSave = (data) => {
        if (info) {
            dispatch(
                clientDiagnosisActions.editClientDiagnosis(info.id, clientId, {
                    startDate: data.startDate,
                    endDate: data.endDate || null,
                    isPrimary: !!data.isPrimary,
                })
            );
        } else {
            dispatch(
                clientDiagnosisActions.createClientDiagnosis(clientId, {
                    code: customCode ? formatDiagnosisCode(data.customCode.trim()) : data.code,
                    ...(customCode && { description: data.customDescription.trim() }),
                    startDate: data.startDate,
                    endDate: data.endDate || undefined,
                    isPrimary: !!data.isPrimary,
                })
            );
        }
    };

    const startDate = watch('startDate');
    const endDate = watch('endDate');
    // Edit subtitle: "Client Name · F84.0 · Description" (the client is the one whose tab is open)
    const clientName = !clientIdProp && client ? [client.firstName, client.lastName].filter(Boolean).join(' ') : '';
    const subtitle = info ? [clientName, info.code, info.description].filter(Boolean).join(' · ') : '';

    return (
        <div className="diagnosis-modal">
            <p className="modal-header-title">{info ? 'Edit Diagnosis' : 'Add Diagnosis'}</p>
            {info && <p className="diagnosis-modal-subtitle">{subtitle}</p>}
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="modal-body-wrapper">
                    {!info && !customCode && (
                        <>
                            <Controller
                                name="code"
                                control={control}
                                rules={{ required: 'Diagnosis code is required.' }}
                                render={({ field, fieldState }) => (
                                    <Icd10CodeSelect
                                        label="Diagnosis code*"
                                        value={field.value}
                                        onSelect={(option) => field.onChange(option?.code || '')}
                                        onAddCustom={startCustomCode}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                            <p className="diagnosis-modal-hint">
                                Search the official ICD-10-CM list. For a code that isn't in it, use Add Diagnosis Code.
                            </p>
                            {!!acceptedCodes?.length && (
                                <p className="diagnosis-modal-hint">{`This service accepts ${acceptedCodes.join(', ')}.`}</p>
                            )}
                        </>
                    )}
                    {!info && customCode && (
                        <>
                            <Input
                                name="customCode"
                                label="Diagnosis Code*"
                                placeholder="e.g. F84.0"
                                variant="outlined"
                                control={control}
                                maxLength={MAX_DIAGNOSIS_CODE_LENGTH}
                                rules={{ validate: (value) => !!value?.trim() || 'Diagnosis code is required.' }}
                            />
                            <Input
                                name="customDescription"
                                label="Description*"
                                placeholder="What the code means"
                                variant="outlined"
                                control={control}
                                maxLength={MAX_DIAGNOSIS_DESCRIPTION_LENGTH}
                                rules={{ validate: (value) => !!value?.trim() || 'Description is required.' }}
                            />
                            <button type="button" className="diagnosis-modal-link" onClick={() => setCustomCode(false)}>
                                Search the ICD-10-CM list instead
                            </button>
                        </>
                    )}
                    <div className="diagnosis-modal-dates">
                        <DateCalendarForm
                            name="startDate"
                            label="Start Date*"
                            control={control}
                            max={endDate ? moment.utc(endDate).format('YYYY-MM-DD') : ''}
                            rules={{ required: 'Start date is required.' }}
                        />
                        <DateCalendarForm
                            name="endDate"
                            label="End Date"
                            control={control}
                            min={startDate ? moment.utc(startDate).format('YYYY-MM-DD') : ''}
                        />
                    </div>
                    {info && <p className="diagnosis-modal-hint">Status updates automatically from the diagnosis start and end dates.</p>}

                    <div className="diagnosis-modal-primary">
                        <p>Set as primary diagnosis</p>
                        <SwitchCustomized name="isPrimary" control={control} checkedBgColor={'#0C66E4'} />
                    </div>
                    {watch('isPrimary') && otherPrimary && (
                        <div className="diagnosis-modal-warning">
                            <img src={Images.alertWarning} alt="warning" />
                            <p>{`Setting this as primary will replace ${otherPrimary.code} as the client's primary diagnosis.`}</p>
                        </div>
                    )}

                    {backError?.error && <ErrMessage text={backError.error} />}
                </div>
                <CreateChancel
                    type="submit"
                    loader={!!loader.length}
                    create={info ? 'Save Changes' : 'Add'}
                    chancel={'Cancel'}
                    onClose={closeModal}
                    buttonWidth="100%"
                    cancelRight={'24px'}
                />
            </form>
        </div>
    );
};
